<template>
    <div class="max-w-4xl mx-auto py-10 px-4">
        <div class="mb-8 text-center">
            <h1 class="text-3xl font-bold text-slate-800 mb-2 tracking-tight">
                Generatore Esercizi <span class="text-blue-600">CRUD</span>
            </h1>
            <p class="text-slate-500 font-medium">Configura e genera tracce per laboratori di informatica</p>
        </div>

        <!-- API Key Input -->
        <div class="mb-6 bg-white p-6 rounded-lg shadow-sm border border-slate-200">
            <div class="flex justify-between items-center mb-2">
                <label class="block text-sm font-bold text-slate-700">Gemini API Key</label>
                <button @click="showGuideModal = true"
                    class="text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Come ottenere una API Key?
                </button>
            </div>
            <div class="relative">
                <input v-model="apiKey" :type="showApiKey ? 'text' : 'password'"
                    placeholder="Incolla qui la tua Gemini API Key..."
                    class="w-full pl-4 pr-10 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all font-mono text-sm" />
                <div class="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer text-slate-400 hover:text-blue-600"
                    @click="toggleApiKeyVisibility" title="Mostra/Nascondi">
                    <svg v-if="!showApiKey" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none"
                        viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                </div>
            </div>
            <p class="text-xs text-slate-500 mt-2 flex items-start gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" fill="none"
                    viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span>
                    <strong>Privacy & Sicurezza:</strong> La tua API Key viene utilizzata esclusivamente per questa
                    sessione e
                    <span class="font-bold text-red-600">NON viene salvata</span> sui nostri server né nel localStorage
                    del browser.
                    Per tua tutela, dovrai reinserirla aggiornando la pagina.
                </span>
            </p>
            <p class="text-xs text-slate-500 mt-2 flex items-start gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none"
                    viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                <span>
                    <strong>Generazione AI:</strong> I documenti e le tracce delle esercitazioni sono generati
                    interamente da un modello di Intelligenza Artificiale (Google Gemini). Si consiglia sempre una
                    verifica della traccia da parte dello studente.
                </span>
            </p>
        </div>


        <div class="bg-white p-8 rounded-lg shadow-sm border border-slate-200 border-t-4 border-t-blue-600">

            <!-- Year Selection -->
            <div class="mb-8">
                <label class="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Anno di
                    Corso</label>
                <div class="grid grid-cols-3 gap-4">
                    <button v-for="year in ['3', '4', '5']" :key="year" @click="selectedYear = year" :class="[
                        'py-3 px-4 rounded-md font-bold text-center transition-all border-2',
                        selectedYear === year
                            ? 'bg-slate-800 border-slate-800 text-white shadow-md'
                            : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700'
                    ]">
                        {{ year }}° Anno
                    </button>
                </div>
            </div>

            <!-- Year Specific Options -->
            <transition mode="out-in" enter-active-class="transition duration-200 ease-out"
                enter-from-class="opacity-0 -translate-y-2" enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition duration-150 ease-in" leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 -translate-y-2">
                <div v-if="selectedYear === '4'" class="mb-8 p-5 bg-blue-50 rounded-md border border-blue-100">
                    <label class="block text-sm font-bold text-blue-900 mb-2">Tipologia Classi (4° Anno)</label>
                    <select v-model="classType"
                        class="w-full px-4 py-2 border border-blue-200 rounded bg-white text-blue-900 focus:ring-2 focus:ring-blue-500 outline-none font-medium">
                        <option value="Semplici">Classi Semplici</option>
                        <option value="Composte">Classi Composte</option>
                        <option value="Derivate">Classi Derivate (Ereditarietà)</option>
                    </select>
                </div>

                <div v-else-if="selectedYear === '3' || selectedYear === '5'"
                    class="mb-8 p-5 rounded-md border transition-colors duration-300"
                    :class="[difficultyClasses.bg, difficultyClasses.border]">
                    <label class="block text-sm font-bold mb-2 transition-colors duration-300"
                        :class="difficultyClasses.text">Livello Difficoltà</label>
                    <select v-model="difficulty"
                        class="w-full px-4 py-2 border rounded bg-white focus:ring-2 outline-none transition-colors duration-300 font-medium"
                        :class="[difficultyClasses.inputBorder, difficultyClasses.focus, difficultyClasses.text]">
                        <option value="Facile">Facile</option>
                        <option value="Medio">Medio</option>
                        <option value="Difficile">Difficile</option>
                    </select>
                </div>
            </transition>

            <!-- Scenario Selection -->
            <div class="mb-8">
                <label class="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Scenario</label>
                <div class="flex gap-4 mb-4">
                    <label class="flex items-center cursor-pointer group">
                        <input type="radio" v-model="scenarioMode" value="predefined"
                            class="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300">
                        <span
                            class="ml-2 text-slate-700 font-medium group-hover:text-blue-700 transition-colors">Predefinito</span>
                    </label>
                    <label class="flex items-center cursor-pointer group">
                        <input type="radio" v-model="scenarioMode" value="custom"
                            class="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300">
                        <span
                            class="ml-2 text-slate-700 font-medium group-hover:text-blue-700 transition-colors">Personalizzato
                            (AI)</span>
                    </label>
                </div>

                <!-- Moderation Error Alert -->
                <div v-if="moderationError"
                    class="mb-4 p-3.5 bg-red-50 border-l-4 border-red-500 rounded-r-md text-red-800 text-sm flex items-start justify-between shadow-sm">
                    <div class="flex items-start gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                        </svg>
                        <span class="font-medium">{{ moderationError }}</span>
                    </div>
                    <button @click="moderationError = ''" class="text-red-400 hover:text-red-600 transition-colors ml-2 font-bold" title="Chiudi">
                        &times;
                    </button>
                </div>

                <div v-if="scenarioMode === 'predefined'">
                    <select v-model="selectedScenarioId"
                        class="w-full px-4 py-2.5 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none bg-slate-50 text-slate-700 text-sm">
                        <option v-for="scenario in scenarios" :key="scenario.id" :value="scenario.id">
                            {{ scenario.title }} - {{ scenario.description }}
                        </option>
                    </select>
                </div>
                <div v-else>
                    <input v-model="customScenario" @input="moderationError = ''" type="text" placeholder="Descrivi brevemente lo scenario..."
                        class="w-full px-4 py-2.5 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none bg-slate-50 text-slate-700 text-sm" />
                </div>
            </div>

            <!-- Generate Button -->
            <button @click="generate" :disabled="loading || !apiKey"
                class="w-full py-3.5 bg-blue-600 text-white font-bold rounded-md shadow-md hover:bg-blue-700 hover:shadow-lg transform active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2">
                <span v-if="loading" class="flex items-center justify-center gap-2">
                    <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none"
                        viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4">
                        </circle>
                        <path class="opacity-75" fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                        </path>
                    </svg>
                    Elaborazione...
                </span>
                <span v-else class="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                    Genera Esercizio
                </span>
            </button>

            <p v-if="!apiKey" class="mt-3 text-xs text-red-500 text-center font-medium">⚠️ API Key richiesta</p>
        </div>

        <!-- Result Display -->
        <ExerciseDisplay :text="generatedText" :title="currentScenarioTitle" :year="selectedYear"
            :difficulty="difficulty" />

        <!-- API Key Guide Modal -->
        <ApiKeyModal :is-open="showGuideModal" @close="showGuideModal = false" />
    </div>
</template>

<script>
import { scenarios } from '../data/scenarios';
import { GeminiService } from '../services/gemini';
import { validateCustomScenario, MODERATION_ERROR_MESSAGE, ContentModerationError } from '../services/moderation';
import ExerciseDisplay from '../components/ExerciseDisplay.vue';
import ApiKeyModal from '../components/ApiKeyModal.vue';

export default {
    name: 'GeneratorView',
    components: {
        ExerciseDisplay,
        ApiKeyModal
    },
    data() {
        return {
            apiKey: '',
            showApiKey: false,
            showGuideModal: false,
            selectedYear: '3',
            classType: 'Semplici',
            difficulty: 'Facile',
            scenarioMode: 'predefined',
            selectedScenarioId: 1,
            customScenario: '',
            moderationError: '',
            scenarios: scenarios,
            generatedText: '',
            currentScenarioTitle: '',
            loading: false,
            geminiService: null,
        }
    },
    computed: {
        difficultyClasses() {
            switch (this.difficulty) {
                case 'Facile':
                    return {
                        bg: 'bg-green-50',
                        border: 'border-green-100',
                        text: 'text-green-800',
                        inputBorder: 'border-green-200',
                        focus: 'focus:ring-green-500'
                    };
                case 'Medio':
                    return {
                        bg: 'bg-amber-50',
                        border: 'border-amber-100',
                        text: 'text-amber-800',
                        inputBorder: 'border-amber-200',
                        focus: 'focus:ring-amber-500'
                    };
                case 'Difficile':
                    return {
                        bg: 'bg-red-50',
                        border: 'border-red-100',
                        text: 'text-red-800',
                        inputBorder: 'border-red-200',
                        focus: 'focus:ring-red-500'
                    };
                default:
                    return {
                        bg: 'bg-green-50',
                        border: 'border-green-100',
                        text: 'text-green-800',
                        inputBorder: 'border-green-200',
                        focus: 'focus:ring-green-500'
                    };
            }
        }
    },
    methods: {
        toggleApiKeyVisibility() {
            this.showApiKey = !this.showApiKey;
        },
        handleModerationViolation() {
            this.loading = false;
            this.customScenario = '';
            this.currentScenarioTitle = '';
            this.generatedText = '';
            this.moderationError = MODERATION_ERROR_MESSAGE;
            alert(MODERATION_ERROR_MESSAGE);
        },
        async generate() {
            if (!this.apiKey) return;

            this.moderationError = '';

            // Verifica preventiva delle regole di sicurezza PG-13 per scenari personalizzati
            if (this.scenarioMode === 'custom') {
                const validation = validateCustomScenario(this.customScenario);
                if (!validation.isValid) {
                    this.handleModerationViolation();
                    return;
                }
            }

            this.loading = true;
            this.generatedText = '';

            try {
                if (!this.geminiService || this.geminiService.apiKey !== this.apiKey) {
                    this.geminiService = new GeminiService(this.apiKey);
                }

                let scenarioText = '';
                if (this.scenarioMode === 'predefined') {
                    const s = this.scenarios.find(sc => sc.id === this.selectedScenarioId);
                    scenarioText = s ? `${s.title}: ${s.description}` : '';
                } else {
                    scenarioText = this.customScenario;
                }

                const options = {
                    classType: this.selectedYear === '4' ? this.classType : null,
                    difficulty: (this.selectedYear === '3' || this.selectedYear === '5') ? this.difficulty : null,
                    isCustom: this.scenarioMode === 'custom'
                };

                // Set title for PDF
                if (this.scenarioMode === 'predefined') {
                    const s = this.scenarios.find(sc => sc.id === this.selectedScenarioId);
                    this.currentScenarioTitle = s ? s.title : 'Esercizio';
                } else {
                    this.currentScenarioTitle = this.customScenario || 'Generazione AI';
                }

                const result = await this.geminiService.generateExercise(this.selectedYear, scenarioText, options);
                this.generatedText = result;

            } catch (error) {
                console.error(error);
                if (error instanceof ContentModerationError || error.isModerationViolation) {
                    this.handleModerationViolation();
                } else if (error && (error.message || '').includes('503')) {
                    alert("I server di Google Gemini stanno riscontrando un traffico elevato temporaneo (errore 503). Attendi qualche istante e riprova.");
                } else {
                    alert("Errore durante la generazione. Controlla la console e la tua API Key.");
                }
            } finally {
                this.loading = false;
            }
        }
    }
}
</script>
